/**
 * Keyword product search used by the POS sale grid, the pairing modal and the
 * printer-profile product picker (re-exported from components/PairingModal).
 *
 * Results are identical to the original matcher: a product matches a word when
 * the diacritics-stripped word is found (case-insensitively) in its
 * diacritics-stripped Hungarian name, and results are ordered by the number of
 * matched words (ties keep catalog order). What changed is only the cost per
 * keystroke on low-end devices:
 *  - per-word cleaning and the RegExp are built once per query, not once per
 *    product;
 *  - the Unicode NFD normalisation of each product name is cached across
 *    keystrokes (it is by far the most expensive step on Hermes/Android);
 *  - the final id → product resolution is a single map instead of an
 *    `array.find` over the whole catalog for every match (O(n²)).
 */

const COMBINING_MARKS = /[̀-ͯ]/g;
const NON_ALPHANUMERIC = /[^A-Za-z0-9\s]/g;

/** Bound for the normalised-name cache; cleared wholesale when exceeded. */
const MAX_CACHED_NAMES = 5000;

const strippedNameCache = new Map();

/**
 * Strip accents and every non-alphanumeric character (whitespace is kept).
 *
 * @param {string} text
 * @returns {string}
 */
export const removeDiacritics = (text) => {
    return String(text).normalize('NFD').replace(COMBINING_MARKS, '').replace(NON_ALPHANUMERIC, '');
};

const getStrippedName = (name) => {
    let stripped = strippedNameCache.get(name);

    if (stripped === undefined) {
        if (strippedNameCache.size >= MAX_CACHED_NAMES) {
            strippedNameCache.clear();
        }
        stripped = removeDiacritics(name);
        strippedNameCache.set(name, stripped);
    }

    return stripped;
};

const getHungarianName = (food) => {
    const name = food?.name;

    if (typeof name === 'string') {
        return name;
    }

    return typeof name?.hu === 'string' ? name.hu : '';
};

/**
 * Search products by space-separated keywords.
 *
 * @param {string} [keywords=""] Raw search input.
 * @param {Array<object>} [array=[]] Products (`{ _id, name: { hu } }`).
 * @returns {Array<object>} Matching products, most matched words first.
 */
export const searchFoodByKeywords = (keywords = "", array = []) => {
    const words = String(keywords ?? '').split(' ').filter(word => word.length > 0);

    // If no search words, return empty array
    if (words.length === 0 || !Array.isArray(array)) {
        return [];
    }

    const terms = words.map((word) => {
        const cleanedWord = removeDiacritics(word.replace('(', '').replace(')', ''));

        return {
            word,
            // No `g` flag: a shared global RegExp would carry `lastIndex`
            // between products; a fresh /…/gi test and a /…/i test agree.
            regExp: new RegExp(cleanedWord, 'i'),
            lowerWord: cleanedWord.toLowerCase(),
        };
    });

    // id → { food: first product with that id, matched: [words] }, in catalog order.
    const found = new Map();

    array.forEach((food) => {
        const id = food?._id;
        let entry = found.get(id);

        if (!entry) {
            entry = { food, matched: [] };
            found.set(id, entry);
        }

        const name = getHungarianName(food);
        const strippedName = getStrippedName(name);
        const lowerName = name.toLowerCase();

        terms.forEach(({ word, regExp, lowerWord }) => {
            if (regExp.test(strippedName) || lowerName.includes(lowerWord)) {
                entry.matched.push(word);
            }
        });
    });

    return Array.from(found.values())
        .filter(entry => entry.matched.length > 0)
        .sort((a, b) => b.matched.length - a.matched.length)
        .map(entry => entry.food);
};

/** Whitespace-free, lower-case form of a barcode / SKU (mirrors scanInput.normalizeScanCode). */
const normalizeCode = (value) => String(value ?? '').replace(/\s+/g, '').toLowerCase();

/**
 * Exact barcode / SKU lookup over the cached POS catalog: the product-level
 * `barcode` / `sku` and every size's `barcode` / `sku`. Name search never sees
 * codes, so a scanned or typed EAN used to find nothing.
 *
 * @param {string} code Scanned or typed code (ignored below 3 characters).
 * @param {Array<object>} [foods=[]] Catalog products.
 * @returns {Array<{food: object, size: (object|null)}>} One entry per matching
 *   product, in catalog order; `size` is the matching size, or null for a
 *   product-level match.
 */
export const findFoodsByCode = (code, foods = []) => {
    const needle = normalizeCode(code);

    if (needle.length < 3 || !Array.isArray(foods)) {
        return [];
    }

    const matches = [];
    const seen = new Set();

    foods.forEach((food) => {
        if (!food || seen.has(food._id)) {
            return;
        }

        const productMatch = normalizeCode(food.barcode) === needle || normalizeCode(food.sku) === needle;
        const size = productMatch
            ? null
            : (Array.isArray(food.sizes) ? food.sizes : []).find((item) => (
                normalizeCode(item?.barcode) === needle || normalizeCode(item?.sku) === needle
            ));

        if (productMatch || size) {
            seen.add(food._id);
            matches.push({food, size: size || null});
        }
    });

    return matches;
};
